import { useState, useEffect } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search, FlaskConical, ArrowRight, ExternalLink, ChevronRight, ArrowLeft } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import {
  useCompoundSearch,
  useCompoundReactions,
  useReactionProteins,
  CompoundSearchResult,
  CompoundReaction,
} from '../api/hooks'

type NavigationLevel = 'search' | 'reactions' | 'proteins'

interface SelectedCompound {
  chebi_accession: string
  name?: string
}

interface SelectedReaction {
  rhea_id: string
  equation?: string
}

export function ReactionSearchPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const chebiParam = searchParams.get('chebi') || searchParams.get('compound')
  const searchParam = searchParams.get('search')

  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [level, setLevel] = useState<NavigationLevel>('search')
  const [selectedCompound, setSelectedCompound] = useState<SelectedCompound | null>(null)
  const [selectedReaction, setSelectedReaction] = useState<SelectedReaction | null>(null)

  // Auto-select compound if chebi param is provided (from Substructure search link)
  useEffect(() => {
    if (chebiParam) {
      setSelectedCompound({
        chebi_accession: chebiParam,
        name: chebiParam,
      })
      setLevel('reactions')
    }
  }, [chebiParam])

  // Auto-search if search param is provided (from Command Palette)
  useEffect(() => {
    if (searchParam && !chebiParam) {
      setQuery(searchParam)
      setSearchTerm(searchParam)
      setLevel('search')
    }
  }, [searchParam, chebiParam])

  const { data: compoundResults, isLoading: compoundLoading } = useCompoundSearch(searchTerm)
  const { data: reactionResults, isLoading: reactionLoading } = useCompoundReactions(
    selectedCompound?.chebi_accession || ''
  )
  const { data: proteinResults, isLoading: proteinLoading } = useReactionProteins(
    selectedReaction?.rhea_id || '',
    selectedCompound?.chebi_accession || ''
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    setSearchTerm(query.trim())
    setLevel('search')
    setSelectedCompound(null)
    setSelectedReaction(null)
  }

  const handleSelectCompound = (compound: CompoundSearchResult) => {
    setSelectedCompound({
      chebi_accession: compound.chebi_accession,
      name: compound.name,
    })
    setLevel('reactions')
    setSelectedReaction(null)
  }

  const handleSelectReaction = (reaction: CompoundReaction) => {
    setSelectedReaction({
      rhea_id: reaction.rhea_id,
      equation: reaction.equation,
    })
    setLevel('proteins')
  }

  const handleBackToSearch = () => {
    setLevel('search')
    setSelectedCompound(null)
    setSelectedReaction(null)
  }

  const handleBackToReactions = () => {
    setLevel('reactions')
    setSelectedReaction(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Reaction Search</h1>
        <p className="text-gray-400 mt-1">
          Search for reaction participants to find associated MIBiG gene clusters
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-accent-green" />
            Search Reaction Participants
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <Input
              placeholder="Search by compound name (e.g., ATP, NADH, acetyl-CoA)..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              leftIcon={<Search className="w-5 h-5" />}
            />
            <div className="flex flex-wrap gap-2">
              <p className="text-sm text-gray-400">Examples:</p>
              {['ATP', 'NADH', 'acetyl-CoA', 'pyruvate', 'adenosyl'].map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => {
                    setQuery(example)
                    setSearchTerm(example)
                    setLevel('search')
                    setSelectedCompound(null)
                    setSelectedReaction(null)
                  }}
                  className="text-sm text-accent-cyan hover:underline"
                >
                  {example}
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                leftIcon={<Search className="w-4 h-4" />}
                disabled={!query.trim()}
              >
                Search
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Breadcrumb Navigation */}
      {searchTerm && (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <button
            onClick={handleBackToSearch}
            className={`hover:text-white transition-colors ${level === 'search' ? 'text-accent-green font-medium' : ''}`}
          >
            Compounds
          </button>
          {selectedCompound && (
            <>
              <ChevronRight className="w-4 h-4" />
              <button
                onClick={handleBackToReactions}
                className={`hover:text-white transition-colors ${level === 'reactions' ? 'text-accent-green font-medium' : ''}`}
                dangerouslySetInnerHTML={{ __html: selectedCompound.name || selectedCompound.chebi_accession }}
              />
            </>
          )}
          {selectedReaction && (
            <>
              <ChevronRight className="w-4 h-4" />
              <span className="text-accent-green font-medium">
                {selectedReaction.rhea_id}
              </span>
            </>
          )}
        </div>
      )}

      {/* Level 1: Compound Search Results */}
      {searchTerm && level === 'search' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>ChEBI Compounds</CardTitle>
                {compoundResults && (
                  <Badge variant="cyan">
                    {compoundResults.total} found
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {compoundLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : compoundResults?.data && compoundResults.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Structure</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">ChEBI ID</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Formula</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {compoundResults.data.map((compound, idx) => (
                        <motion.tr
                          key={compound.chebi_accession}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                          onClick={() => handleSelectCompound(compound)}
                        >
                          <td className="py-3 px-4">
                            {compound.structure_url ? (
                              <a
                                href={compound.structure_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-12 h-12 bg-white rounded overflow-hidden hover:ring-2 hover:ring-accent-cyan/50 transition-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <img
                                  src={compound.structure_url}
                                  alt={compound.name || compound.chebi_accession}
                                  className="w-full h-full object-contain"
                                  loading="lazy"
                                  onError={(e) => {
                                    const parent = (e.target as HTMLImageElement).parentElement
                                    if (parent) {
                                      parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 text-xs">N/A</div>'
                                      parent.className = 'block w-12 h-12 bg-gray-700 rounded overflow-hidden'
                                    }
                                  }}
                                />
                              </a>
                            ) : (
                              <div className="w-12 h-12 bg-gray-700 rounded flex items-center justify-center text-gray-400 text-xs">
                                N/A
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <a
                              href={`https://www.ebi.ac.uk/chebi/searchId.do?chebiId=${compound.chebi_accession}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-accent-cyan hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {compound.chebi_accession}
                            </a>
                          </td>
                          <td className="py-3 px-4 text-gray-300 max-w-xs">
                            <div
                              className="truncate"
                              dangerouslySetInnerHTML={{ __html: compound.name || '-' }}
                            />
                          </td>
                          <td className="py-3 px-4 text-gray-400 font-mono text-xs">
                            {compound.formula || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              rightIcon={<ArrowRight className="w-4 h-4" />}
                              onClick={() => handleSelectCompound(compound)}
                            >
                              Reactions
                            </Button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No compounds found matching "{searchTerm}".
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Level 2: Reactions for Compound */}
      {level === 'reactions' && selectedCompound && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<ArrowLeft className="w-4 h-4" />}
                    onClick={handleBackToSearch}
                  >
                    Back
                  </Button>
                  <CardTitle>
                    Reactions involving{' '}
                    <span
                      className="text-accent-green"
                      dangerouslySetInnerHTML={{ __html: selectedCompound.name || selectedCompound.chebi_accession }}
                    />
                  </CardTitle>
                </div>
                {reactionResults && (
                  <Badge variant="green">
                    {reactionResults.total} reactions
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {reactionLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : reactionResults?.data && reactionResults.data.length > 0 ? (
                <div className="space-y-4">
                  {reactionResults.data.map((reaction, idx) => (
                    <motion.div
                      key={reaction.rhea_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 bg-white/5 rounded-lg border border-white/5 hover:border-accent-green/30 transition-colors cursor-pointer"
                      onClick={() => handleSelectReaction(reaction)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {reaction.rhea_url ? (
                              <a
                                href={reaction.rhea_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-accent-green hover:underline inline-flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {reaction.rhea_id}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="font-mono text-gray-400">
                                {reaction.rhea_id}
                              </span>
                            )}
                            <Badge variant="purple">
                              {reaction.gene_count} MIBiG hits
                            </Badge>
                          </div>
                          <p className="text-gray-300 text-sm">{reaction.equation || 'Equation not available'}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          rightIcon={<ArrowRight className="w-4 h-4" />}
                          onClick={() => handleSelectReaction(reaction)}
                        >
                          View Proteins
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No reactions found for this compound.
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Level 3: Proteins for Reaction */}
      {level === 'proteins' && selectedCompound && selectedReaction && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<ArrowLeft className="w-4 h-4" />}
                    onClick={handleBackToReactions}
                  >
                    Back
                  </Button>
                  <CardTitle>
                    MIBiG Proteins for{' '}
                    <span className="text-accent-green">{selectedReaction.rhea_id}</span>
                  </CardTitle>
                </div>
                {proteinResults && (
                  <Badge variant="purple">
                    {proteinResults.total} proteins
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selectedReaction.equation && (
                <div className="mb-4 p-3 bg-white/5 rounded-lg text-sm text-gray-300">
                  <span className="text-gray-500">Reaction: </span>
                  {selectedReaction.equation}
                </div>
              )}
              {proteinLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : proteinResults?.data && proteinResults.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">MIBiG</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Protein</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Organism</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Product</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">E-value</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">SwissProt Hits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proteinResults.data.map((protein, idx) => (
                        <motion.tr
                          key={`${protein.mibig_accession}-${protein.protein_id}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          className="border-b border-white/5 hover:bg-white/5"
                        >
                          <td className="py-3 px-4">
                            <Link
                              to={`/clusters/${protein.mibig_accession}`}
                              className="font-mono text-accent-cyan hover:underline"
                            >
                              {protein.mibig_accession}
                            </Link>
                          </td>
                          <td className="py-3 px-4">
                            <Link
                              to={`/proteins/${protein.mibig_accession}/${protein.protein_id}`}
                              state={{ from: location.pathname + location.search }}
                              className="font-mono text-accent-purple hover:underline"
                            >
                              {protein.protein_id}
                            </Link>
                          </td>
                          <td className="py-3 px-4 text-gray-300 max-w-xs truncate" title={protein.organism_name}>
                            {protein.organism_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-gray-400 max-w-xs truncate" title={protein.product}>
                            {protein.product || '-'}
                          </td>
                          <td className="py-3 px-4 text-gray-400 font-mono">
                            {protein.evalue ? protein.evalue.toExponential(2) : '-'}
                          </td>
                          <td className="py-3 px-4">
                            {protein.swissprot_hit_count && protein.swissprot_hit_count > 0 ? (
                              <Link
                                to={`/proteins/${protein.mibig_accession}/${protein.protein_id}/swissprot-hits?rhea_accession=${encodeURIComponent(selectedReaction.rhea_id)}`}
                                state={{ from: location.pathname + location.search }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-green/20 text-accent-green rounded hover:bg-accent-green/30 transition-colors"
                              >
                                {protein.swissprot_hit_count}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : (
                              <span className="text-gray-500">0</span>
                            )}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No proteins found for this reaction.
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Info Card */}
      {!searchTerm && (
        <Card variant="solid">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <FlaskConical className="w-8 h-8 text-accent-green flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  About Reaction Search
                </h3>
                <p className="text-gray-400 mb-4">
                  This tool allows you to navigate from reaction participants (ChEBI compounds)
                  to Rhea reactions and finally to the MIBiG gene cluster proteins that catalyze
                  these reactions.
                </p>
                <div className="text-sm text-gray-500">
                  <p className="font-medium text-gray-400 mb-2">Navigation Flow:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Search for a compound by name (e.g., "ATP")</li>
                    <li>Select a compound to view associated reactions</li>
                    <li>Select a reaction to view MIBiG proteins that catalyze it</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
